import { DOUBLES_SINGLES } from "./signupSheets/signupSheetModel";

export const DEFAULT_DOUBLES_SINGLES_SETUP = {
  shareSquad: true,
  afterDoubles: DOUBLES_SINGLES.optional,
};

export const normalizeDoublesSinglesSetup = (value) => {
  const shareSquad = value?.shareSquad !== false;
  const afterDoubles =
    value?.afterDoubles === DOUBLES_SINGLES.always ||
    value?.afterDoubles === DOUBLES_SINGLES.doublesOnly
      ? value.afterDoubles
      : DOUBLES_SINGLES.optional;
  return {
    shareSquad,
    afterDoubles: shareSquad ? afterDoubles : DOUBLES_SINGLES.doublesOnly,
  };
};

export const effectiveDoublesSinglesSetup = (row, mixedSquads) =>
  normalizeDoublesSinglesSetup({
    shareSquad: Boolean(mixedSquads),
    afterDoubles: row?.afterDoubles,
  });

export const setupFromRow = (row) =>
  normalizeDoublesSinglesSetup({
    shareSquad: row?.shareSquad,
    afterDoubles: row?.afterDoubles,
  });

export const tournamentHasDoublesAndSingles = (events) => {
  const primaries = (events || []).filter(
    (event) => event && event.isDeleted !== true && event.isPrimary !== false
  );
  const hasDoubles = primaries.some(
    (event) => Number(event.bowlerQuantity) === 2
  );
  const hasSingles = primaries.some(
    (event) => Number(event.bowlerQuantity) === 1
  );
  return hasDoubles && hasSingles;
};

export const printDoublesSinglesMode = (setup) => {
  const normalized = normalizeDoublesSinglesSetup(setup);
  if (!normalized.shareSquad) return DOUBLES_SINGLES.doublesOnly;
  return normalized.afterDoubles;
};

export const shareSquadSummary = (setup) =>
  normalizeDoublesSinglesSetup(setup).shareSquad ? "Yes" : "No";

export const afterDoublesSummary = (setup) => {
  const mode = printDoublesSinglesMode(setup);
  if (mode === DOUBLES_SINGLES.always) return "Everyone";
  if (mode === DOUBLES_SINGLES.doublesOnly) return "Not on the Doubles signup sheet";
  return "Only if they mark it";
};

export const afterDoublesBowlerCopy = (setup) => {
  const normalized = normalizeDoublesSinglesSetup(setup);
  if (!normalized.shareSquad) return "";
  if (normalized.afterDoubles === DOUBLES_SINGLES.always) {
    return "Everyone bowls singles after doubles";
  }
  if (normalized.afterDoubles === DOUBLES_SINGLES.doublesOnly) {
    return "Doubles does not include singles";
  }
  return "Bowlers bowl singles after doubles only if they mark it";
};

export const printDoublesFollowsSetupLabel = (setup) =>
  `Doubles follows Setup: ${afterDoublesSummary(setup)}.`;

export const singlesFollowsDoublesOnline = (setup) => {
  const mode = printDoublesSinglesMode(setup);
  return (
    mode === DOUBLES_SINGLES.optional || mode === DOUBLES_SINGLES.always
  );
};

export const onlineCompanionSinglesNote = (setup) => {
  const mode = printDoublesSinglesMode(setup);
  if (mode === DOUBLES_SINGLES.always) {
    return "Singles are added as part of Doubles.";
  }
  if (mode === DOUBLES_SINGLES.optional) {
    return "Singles are added as part of Doubles. Mark each bowler on Doubles.";
  }
  return null;
};

export const showCreateTeamSinglesToggle = ({
  event,
  setup,
  singlesEvent,
} = {}) =>
  Number(event?.bowlerQuantity) === 2 &&
  Boolean(singlesEvent) &&
  printDoublesSinglesMode(setup) !== DOUBLES_SINGLES.doublesOnly;

export const showCreateTeamEveryoneSinglesNote = ({
  event,
  setup,
  singlesEvent,
} = {}) =>
  Number(event?.bowlerQuantity) === 2 &&
  Boolean(singlesEvent) &&
  printDoublesSinglesMode(setup) === DOUBLES_SINGLES.always;

export const createTeamEveryoneSinglesNote =
  "After you save this Doubles team, Create Team opens for Singles for each bowler. Set side pots on that form.";

export const createTeamOptionalSinglesNote =
  "After you save this Doubles team, Create Team opens for Singles for each bowler you mark. Set side pots on that form.";

export const createTeamSinglesFollowUpNote = (name, remainingCount = 1) => {
  const who = String(name || "").trim() || "this bowler";
  const more =
    Number(remainingCount) > 1
      ? ` Then ${Number(remainingCount) - 1} more.`
      : "";
  return `Doubles team saved. This is the Singles team for ${who}.${more} Set side pots, then Save Team.`;
};

export const snapshotBowlerForSinglesFollowUp = (bowler) => {
  const name = String(bowler?.name || "").trim();
  if (!name) return null;
  return {
    name,
    average: bowler.average ?? "",
    hasAverage: bowler.hasAverage !== false,
    usbcID: bowler.usbcID || "",
    email: bowler.email || "",
    phone: bowler.phone || "",
    existingBowler: Boolean(bowler.existingBowler),
    id: bowler.existingBowler ? bowler.id || null : null,
    catalogMatch: bowler.catalogMatch || null,
  };
};

export const bowlersForSinglesFollowUp = ({
  event,
  setup,
  bowlers,
  singlesEvent,
} = {}) => {
  if (Number(event?.bowlerQuantity) !== 2 || !singlesEvent) return [];
  if (printDoublesSinglesMode(setup) === DOUBLES_SINGLES.doublesOnly) return [];
  return (bowlers || [])
    .filter((bowler) => bowler?.enterSingles)
    .map(snapshotBowlerForSinglesFollowUp)
    .filter(Boolean);
};

export const createTeamBowlerFromSinglesFollowUp = (snapshot, index = 0) => ({
  id:
    snapshot?.existingBowler && snapshot?.id
      ? snapshot.id
      : `temp-singles-${index}`,
  name: snapshot?.name || "",
  average: snapshot?.average ?? "",
  usbcID: snapshot?.usbcID || "",
  email: snapshot?.email || "",
  phone: snapshot?.phone || "",
  existingBowler: Boolean(snapshot?.existingBowler),
  hasAverage: snapshot?.hasAverage !== false,
  averagePopulated: false,
  selectedSidePots: {},
  bracketTickets: {},
  eventEntries: [],
  enterSingles: false,
  isLookupOpen: false,
  lookupResults: [],
  catalogSuggestions: [],
  catalogMatch: snapshot?.catalogMatch || null,
  errors: {},
});
